import ErrorsServerInterface from 'app/interfaces/ErrorsServerInterface';
import {casinoGamesStore, configStore} from 'app/store/global';
import {UNPROCESSABLE_ENTITY} from 'app/utils/HttpStatuses';
import Get from 'app/utils/Get';
import Post from 'app/utils/Post';
import {toastError} from 'app/components/toasts/liteToast';
import logEvent from 'app/amplitude/log_event';
import {CasinoGameInterface, CasinoGamesStoreInterface} from 'app/interfaces/stores/CasinoGamesStoreInterfaces';
import I18n from 'app/i18n';
import {arraySame} from 'app/utils/ArraySame';
import {ELEMENT_NOT_FOUND} from 'app/utils/Consts';
import {isDesktop} from 'app/utils';
import {getIframeGridPlacement} from 'app/components/pages/casino/marketing-blocks/casinoGridRow';
import {scrollCasinoGridIframeIntoView} from 'app/components/pages/casino/marketing-blocks/casinoGridScroll';

const HOME_PAGE_GAMES_LENGTH = 21;

interface LoadMoreGamesInterface {
    (data: CasinoGamesStoreInterface): void;
}

interface QueryParamsInterface {
    page?: number,
    order_by?: string,
    favorite?: boolean,
    find?: string,
    game_id?: string,
    provider?: string[],
    categories?: string[]
}

export const casinoGameById = (id: number, filterId: number): CasinoGameInterface => {
    if (filterId) {
        return casinoGamesStore.casinoFilters.find(item => item.id === filterId)
            ?.casinoGames.find(item => item.id === id);
    }
    return casinoGamesStore.data.casinoGames.find(item => item.id === id) ||
        casinoGamesStore.randomSlotGames.find(item => item.id === id) ||
        casinoGamesStore.homePageGames.find(item => item.id === id);
};


export const startCasinoGame = (
    id: number,
    filterId = null,
    el: HTMLElement | null = null
): void => {
    captureCasinoGameLaunchRow(el);
    startCasino(id, filterId);
};

export const startCasino = (id: number, filterId: number, demo = false): void => {
    let casinoGame = casinoGameById(id, filterId);
    const filter = filterId ? casinoGamesStore.casinoFilters.find(item => item.id === filterId) : null;

    if (casinoGame) {
        if (demo) {
            startDemo(casinoGame.id, filter);
        } else {
            startGame(casinoGame.id, filter);
        }
    } else {
        sendRequestFindCasinoGame(id).then(() => {
            casinoGame = casinoGameById(id, filterId);
            if (casinoGame) {
                if (demo) {
                    startDemo(casinoGame.id, filter);
                } else {
                    startGame(casinoGame.id, filter);
                }
            }
        });
    }
};

export const sendRequestFindCasinoGame = async(id: number): Promise<void> => {
    await new Get({
        url: `/casino/games/${id}/load_game`
    })
        .execute()
        .then(response => response.json())
        .then(response => {
            if (response.data) {
                casinoGamesStore.updateGamesCollection(casinoGamesStore.data.casinoGames.concat([response.data]));
            } else if (response.message) {
                toastError(response.message);
            } else {
                toastError(I18n.t('server_error_try_later'));
            }
        });
};

export const favoriteGameChange = (id: number, favorite: boolean, filterId: number): void => {
    new Post({
        params: {
            favorite
        },
        url: `/casino/games/${id}/change_favorite`
    })
        .execute()
        .then(response => response.json())
        .then(response => {
            if (!response.success) {
                toastError(response.message);
            } else if (favorite) {
                const casinoGame = casinoGameById(id, filterId);

                logEvent('CASINO_FAVORITES_ADDED', {
                    game: casinoGame.name,
                    game_type: casinoGame.category,
                    provider: casinoGame.provider_name
                });
            }
        });
};

export const sendRequestStartCasino = (url: string, device: string): void => {
    new Post({
        params: {
            client_type: device
        },
        url
    })
        .execute()
        .then(response => {
            if (!response.ok) {
                throw response;
            }
            return response.json();
        })
        .catch((response: Response) => {
            if (response.status === UNPROCESSABLE_ENTITY) {
                response.json().then(({errors}: {errors: ErrorsServerInterface}) => {
                    toastError(errors.base[0]);
                });
            }
        });
};

export const loadGames = (
    params: QueryParamsInterface,
    savePartCasinoGames: LoadMoreGamesInterface
): void => {
    new Get({
        params: {
            device: casinoGamesStore.device,
            ...params
        },
        url: '/casino/games/load_games'
    })
        .execute()
        .then(response => response.json())
        .then(response => {
            if (!response) {
                toastError(I18n.t('server_error_try_later'));
            }
            savePartCasinoGames(response);
        });
};

export const loadHomePageGames = (callback: ()=> void): void => {
    new Get({
        params: {
            filter_id: configStore.CONFIG?.home_page_casino_filter_id ?? ELEMENT_NOT_FOUND
        },
        url: '/casino/games/games_by_filters'
    }).execute()
        .then(response => response.json())
        .then(result => {
            if (result?.[0]?.casinoGames?.length) {
                casinoGamesStore.updateHomePageGames(result[0].casinoGames.slice(0, HOME_PAGE_GAMES_LENGTH));
            }
        })
        .finally(callback);
};

export const lastFindSame = (): boolean => {
    const {favorite, find, categories, provider} = casinoGamesStore.filteredParams;

    return casinoGamesStore.lastFind.favorite === favorite &&
        casinoGamesStore.lastFind.find === find &&
        arraySame(casinoGamesStore.lastFind.categories, categories) &&
        arraySame(casinoGamesStore.lastFind.provider, provider);
};

export const captureCasinoGameLaunchRow = (element: HTMLElement | null): void => {
    if (!isDesktop()) {
        casinoGamesStore.setIframeGridPlacement(null);
        return;
    }

    if (casinoGamesStore.isGameActive) {
        casinoGamesStore.setIframeGridPlacement(null);
        requestAnimationFrame(() => {
            casinoGamesStore.setIframeGridPlacement(getIframeGridPlacement(element));
            scrollCasinoGridIframeIntoView();
        });
        return;
    }

    casinoGamesStore.setIframeGridPlacement(getIframeGridPlacement(element));
};

export const startGame = (game_id: number, filter = null): void => {
    const wasGameActive = casinoGamesStore.isGameActive;

    if (casinoGamesStore.playGame({demo: false, filter, id: game_id})) {
        casinoGamesStore.openModal();

        if (!wasGameActive) {
            scrollCasinoGridIframeIntoView();
        }
    }
};

export const startDemo = (game_id: number, filter = null): void => {
    const wasGameActive = casinoGamesStore.isGameActive;

    if (casinoGamesStore.playGame({demo: true, filter, id: game_id})) {
        casinoGamesStore.openModal();

        if (!wasGameActive) {
            scrollCasinoGridIframeIntoView();
        }
    }
};
